import { Fragment } from 'react'
import { addCalendarDays, daysUntil, parseISO } from '../../lib/cadence'
import { pct } from './geometry'
import './viz.css'

export interface TimelineEvent {
  date: string
  label?: string
  done?: boolean
}

export interface TimelineLane {
  label: string
  events: TimelineEvent[]
  offRight?: string
}

interface TimelineProps {
  start: string
  days: number
  today: string
  lanes: TimelineLane[]
  ariaLabel: string
}

// The longest window drawn. A cadence view is two weeks; a year is already far
// past useful. The cap keeps a huge finite `days` from running the tick walk
// for ages, or past the dates toISOString can format.
const MAX_DAYS = 366

// Past this position (percent of the axis) an event label no longer fits to the
// right of its dot and goes to its left; a tick label this far right is
// right-aligned to its tick. Both keep text inside the plot. The percentages
// assume a span-3 module (~279px body: 88px label column + 8px gap leaves a
// ~183px track) and 12px mono text (7.2px a character).
// 70% leaves 55px: a 6-character event label ("Oct 20", 43px) plus its 8px
// offset needs 51px. A narrower track (span 2) would need a lower threshold.
const LABEL_FLIP_AT = 70
// 10% of that track is 18px, the half-width of a 5-character tick label
// ("10/26", 36px).
const TICK_END_AT = 90

/**
 * Cadence axis: `days` calendar days (at most 366) from `start` (inclusive), a
 * today line, Monday ticks, and one lane per job holding dots for its events. A
 * dot is `done` only when its event says so; a past date that never ran stays
 * scheduled. Dates are YYYY-MM-DD and compared as UTC calendar days (the
 * cadence.ts helpers), so the local time zone cannot shift a day. A date that
 * does not parse, or falls outside the window, is not drawn.
 */
export function Timeline({ start, days, today, lanes, ariaLabel }: TimelineProps) {
  // Axis length in days, at most MAX_DAYS. Anything unusable (NaN, ±Infinity,
  // below one day) leaves a negative span, so nothing can be placed.
  const span = Number.isFinite(days) ? Math.min(days, MAX_DAYS) - 1 : -1

  /** 0–100 position of a date on the axis, or null when it is unparseable or outside the window. */
  const xOf = (date: string): number | null => {
    const offset = daysUntil(date, start)
    if (!Number.isFinite(offset) || span < 0 || offset < 0 || offset > span) return null
    return span > 0 ? (offset / span) * 100 : 0
  }

  // addCalendarDays formats through toISOString, which throws on an invalid
  // date, so an unparseable start must not reach it.
  const ticks: Array<{ x: number; label: string }> = []
  if (span >= 0 && !Number.isNaN(parseISO(start).getTime())) {
    for (let offset = 0; offset <= span; offset += 1) {
      const day = parseISO(addCalendarDays(start, offset))
      if (day.getUTCDay() === 1) {
        ticks.push({
          x: span > 0 ? (offset / span) * 100 : 0,
          label: `${day.getUTCMonth() + 1}/${day.getUTCDate()}`,
        })
      }
    }
  }

  const todayX = xOf(today)

  return (
    <div className="viz-timeline" role="img" aria-label={ariaLabel}>
      <div className="viz-timeline-axis">
        {ticks.map(({ x, label }, i) => (
          <span
            className={`viz-timeline-tick${x > TICK_END_AT ? ' viz-timeline-tick--end' : ''}`}
            key={`${label}-${i}`}
            style={{ left: pct(x) }}
          >
            {label}
          </span>
        ))}
      </div>
      {todayX !== null ? (
        <div className="viz-timeline-overlay">
          <span className="viz-timeline-today" style={{ left: pct(todayX) }} />
        </div>
      ) : null}
      {lanes.map((lane, i) => (
        <div className="viz-timeline-lane" key={`${lane.label}-${i}`}>
          <span className="viz-timeline-lane-label" title={lane.label}>
            {lane.label}
          </span>
          <div className="viz-timeline-lane-track">
            {lane.events.map((event, j) => {
              const x = xOf(event.date)
              if (x === null) return null
              return (
                <Fragment key={`${event.date}-${j}`}>
                  <span
                    className={`viz-timeline-dot${event.done ? ' viz-timeline-dot--done' : ''}`}
                    style={{ left: pct(x) }}
                  />
                  {event.label ? (
                    <span
                      className={`viz-timeline-dot-label${x > LABEL_FLIP_AT ? ' viz-timeline-dot-label--left' : ''}`}
                      style={{ left: pct(x) }}
                    >
                      {event.label}
                    </span>
                  ) : null}
                </Fragment>
              )
            })}
            {lane.offRight ? <span className="viz-timeline-chip">{lane.offRight} ›</span> : null}
          </div>
        </div>
      ))}
    </div>
  )
}
